/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/jsx-boolean-value */
import React, { createRef, useRef, useEffect, useMemo, useState, PropsWithChildren } from "react";
import { useDispatch, useSelector } from "react-redux";
import { customMediaQueryMax, media, screenSizeBreakpoints, Header3, mapPageMixin, textCenter, styled, zIndexes } from "@iventis/styles";
import { Node } from "@iventis/domain-model/model/node";
import { Theme, css } from "@emotion/react";
import GeoJSON from "geojson";
import { DrawToolBarButtons, SortPanelButton, ZoomInPanelButton, ZoomOutPanelButton } from "@iventis/map-engine-controls";

import {
    VrPanelButton,
    FocusOnObjectsButton,
    AngleofViewButton,
    CommentPanelButton,
    UserLocationButton,
    BookmarksButton,
} from "@iventis/map-engine-controls/components/panel-buttons";
import { checkAnyModeIsPresent, checkModeIsPresent } from "@iventis/map-engine/src/machines/mode-machine.helpers";
import { machineEventTypes, MapMode } from "@iventis/map-engine/src/machines/map-machines.types";
import { Content } from "@iventis/translations/content/typed-content";
import { MappingEngine } from "@iventis/map-engine/src/types/store-schema";
import { analysisLineLayerId, analysisPolygonLayerId, selectedLayerAreaSelectId, MAPBOX_MAX_ZOOM, MAPBOX_MIN_ZOOM } from "@iventis/map-engine/src/bridge/constants";
import { LoadingComponent, ConfirmationDialogComponent, VerticalToolbar } from "@iventis/components";
import { findNode, findNodesByType, useTreeBrowserInitialise, TreeBrowserState, TreeBrowserSort, findNodes } from "@iventis/tree-browser";
import { toast } from "@iventis/toasts/src/toast";
import {
    useRefEffectValue,
    useSidebarSlider,
    isValidUuid,
    createMapFromArray,
    queryParams,
    Link,
    useKeyboardEvent,
    hasParentOfClass,
    isAdmin,
    canEditAll,
} from "@iventis/utilities";
import { userLocalStorageItems, userLocalStorageManager } from "@iventis/local-storage/src/user-local-storage";
import { useTheme } from "@mui/material";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { TreeBrowserDomainNodeContext as TreeBrowserContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { DrawToolBarComponent } from "@iventis/map-control-components/src/draw-toolbar";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { DomainLayer, SavedMapViewStages } from "@iventis/map-engine/src/state/map.state.types";
import { defaultMappingEngine } from "@iventis/map-engine/src/state/map-state.initial-values";
import { SocketEvent } from "@iventis/types/loading.types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { panelbuttonStyle, mapcontrollbuttonStyle } from "@iventis/map-engine-controls/components/toolbar-style-generators";
import { updateUserPreferences } from "@iventis/permissions/src/auth.slice";
import { isCompositionValid } from "@iventis/map-engine/src/utilities/geojson-helpers";
import { convertMapObjectToLocalGeoJson, isAnalysisLayer } from "@iventis/map-engine/src/utilities/state-helpers";
import { filterByUserLayers } from "@iventis/map-engine/src/utilities/layer.helpers";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { ErrorLarge } from "@iventis/components/src/error-large";
import { ModeOfTransport } from "@iventis/domain-model/model/modeOfTransport";
import { RouteWaypoint } from "@iventis/domain-model/model/routeWaypoint";
import { ResourceTypes } from "@iventis/api/src/connection.slice";
import { IventisErrorBoundaryWithToast, ErrorBoundaryFallbackWebGL } from "@iventis/error-boundaries";
import { useResourceConnection } from "@iventis/api/src/use-resource-connection";
import { UserScope } from "@iventis/domain-model/model/userScope";
import { analysisToolMonitoringMetric, drawMapObjectMetric, incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { getMapObjectNameSystemDatafield, getMapObjectRouteTimeSystemDataField } from "@iventis/datafields";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { DrawingType, CommentModeType, ObjectSelectType, isAnalysisToolLine, EditingAction } from "@iventis/map-engine-controls/components/draw-toolbar-buttons-v2";
import { MapEventTypes } from "@iventis/map-engine";
import { getMapBoundingBoxPolygon } from "./services/bounding-box";
import { sidebar } from "./state/sidebar.slice";
import { map } from "./state/map.slice";
import { SideBarComponent } from "./sidebar";
import { MapContainer, MapContainerRef } from "./map-container";
import { resetSlice, RootDispatch, RootState, rootStore, SliceNames } from "./state/root.store";
import { PopoverComponent } from "./popover";
import { eventStream } from "./event-stream";
import { ImportLayersDialog } from "./import-layers-dialog";
import MemoizedMapNavigationComponent from "./map-navigation";
import { MapViewsPopper } from "./map-views-popper";
import { MapViewsForm } from "./map-views-form";
import { SidebarContent } from "./state/sidebar.slice.types";
import { getSidebarNode, sidebarNodeServices } from "./services/library-api-functions";
import { getSort } from "./plan-library/library-get-initial-values";
import { libraryWebSockets } from "./plan-library/library.machine.web-sockets";
import { MapTour } from "./map-tour";
import { mapTreeBrowserSelection } from "./map-tree-browser-selection";
import { routeWaypointService } from "./services/route-waypoint-service";
import { createSearch } from "./search-result.helpers";
import { FROM_MAP_ROOT } from "./map-constants";
import { useMapWebSockets } from "./map-web-sockets";
import { PlanDateFilterComponent } from "./plan-date-filter";
import { loadMap } from "./state/map.slice.get-map-thunks";
import { useGetLayerThumbnailPreview } from "./hooks/use-layer-thumbnail-preview";
import { DigitalTwinCameraModeButtonsComponent } from "./digital-twin-mode-buttons";
import { MapUndoRedoWrapper } from "./map-undo-redo";
import { PlanVenueBookmarksComponent } from "./plan-venue-bookmarks";
import { deleteMapGroup, patchMapGroup } from "./services/map-groups-api-functions";
import { deleteLayer, patchLayers } from "./services/layer-service";
import { useMapLoading } from "./hooks/use-map-loading";
import { connectToDigitalTwinLicense, patchMapObjectsDataFieldValues, updateMapObject } from "./state/map.slice.thunks";
import { MapRoutePlanner } from "./map-route-planner";
import { useLineSplittingState } from "./hooks/use-line-splitting-state";

// Set this to true if running pixel streaming locally
const USE_LOCAL_UNREAL_IP = false;

const MapRootPage: React.FC<PropsWithChildren<{
    mapId: string;
}>> = ({ mapId }) => {
    const theme = useTheme<Theme>();
    const translate = useIventisTranslate();

    // Decides where to anchor the popover
    const [popoverAnchor, setPopoverAnchor] = useState<Element>();

    const { windowWidth, sliderContextState, minSidebarWidth, setLeftWidth, onMouseDownOnSlider, onDoubleClickOnSlider } = useSidebarSlider(
        (width) => (width <= screenSizeBreakpoints.medium ? 200 : 374),
        () => dispatch(map.actions.resized())
    );

    const [closeMapPopup, setCloseMapPopup] = useState<boolean>(false);
    const isMediumScreenSize = windowWidth <= screenSizeBreakpoints.medium;
    const [showObjectDeletionDialog, setShowObjectDeletionDialog] = useState<boolean>(false);

    const [sidebarHidden, setSidebarHidden] = useState<boolean>(windowWidth <= screenSizeBreakpoints.medium);

    const pageRef = createRef<HTMLDivElement>();

    const mapContainerRef = useRef<MapContainerRef>();

    const {
        layers,
        mapObjectsSelected,
        localToRemoteMapObjectIdsMap,
        showMapObjectInformation,
        mode,
        savedMapViewWizard,
        savedMapViews,
        savedMapViewDefault,
        objectChangesSaving,
        mappingEngine,
        position,
        sidebarContent,
        user,
        userPreferences,
        mapModule,
        showImportLayer,
        isPlacingGeometry,
        modalLayerId,
        projectDataFields,
        currentLevel,
        failedToGetMap,
        functionality,
        showRoutePlanner,
        hideComments,
        commentsDrawingMode,
        drawingModifier,
        selectedAnalysisTool,
        drawingToolTip,
        showBookmarks,
        userConnectedToDigitalTwin,
        webGLSupported,
        isConnectingToDigitalTwin,
    } = useSelector(
        (s: RootState) => ({
            layers: s.mapReducer.mapModule.layers,
            mapObjectsSelected: s.mapReducer.mapModule.mapObjectsSelected,
            localToRemoteMapObjectIdsMap: s.mapReducer.mapModule.localToRemoteMapObjectIdsMap,
            showMapObjectInformation: s.mapReducer.showMapObjectInformation,
            mode: s.mapReducer.mapModule.mode,
            waitingFor: s.mapReducer.waitingFor,
            savedMapViewWizard: s.mapReducer.savedMapViewWizard,
            savedMapViews: s.mapReducer.savedMapViews,
            savedMapViewDefault: s.mapReducer.savedMapViewDefault,
            objectChangesSaving: s.mapReducer.objectsChangesSaving,
            mappingEngine: s.mapReducer.mappingEngine,
            position: s.mapReducer.mapModule.position,
            sidebarContent: s.sidebarReducer.content,
            user: s.auth.user,
            userPreferences: s.auth.userPreferences,
            mapModule: s.mapReducer.mapModule,
            showImportLayer: s.sidebarReducer.showImportLayer,
            isPlacingGeometry: s.mapReducer.isPlacingGeometry,
            modalLayerId: s.sidebarReducer.modalLayer?.id,
            projectDataFields: s.mapReducer.mapModule.projectDataFields,
            currentLevel: s.mapReducer.mapModule.currentLevel,
            failedToGetMap: s.mapReducer.failedToGetMap,
            functionality: s.auth.subscriptionPlan.functionality,
            /** Show the route generator only while in edit mode, a single map object is selected and the map object is a route */
            showRoutePlanner: s.mapReducer.showRoutePlanner,
            hideComments: s.mapReducer.mapModule.hideComments,
            commentsDrawingMode: s.mapReducer.mapModule.commentsDrawingMode,
            drawingModifier: s.mapReducer.mapModule.drawingModifier,
            selectedAnalysisTool: s.mapReducer.selectedAnalysisTool,
            drawingToolTip: s.mapReducer.drawingToolTip,
            showBookmarks: s.mapReducer.showBookmarks,
            userConnectedToDigitalTwin: s.mapReducer.mapModule.digitalTwinEngineData.userConnectedToDigitalTwin,
            webGLSupported: s.mapReducer.webGLSupported,
            isConnectingToDigitalTwin: s.mapReducer.mapModule.digitalTwinEngineData.isConnecting,
        }),
        (left, right) => {
            const leftEntries = Object.entries(left);

            const allSame = leftEntries.every(([key, leftValue]) => {
                const rightValue = right[key];

                if (key === "mode") {
                    const leftMode = (leftValue as { value: MapMode }).value;
                    const rightMode = (rightValue as { value: MapMode }).value;

                    if (leftMode.includes("composition") && rightMode.includes("composition")) {
                        return true;
                    }
                }
                return Object.is(rightValue, leftValue);
            });

            return allSame;
        }
    );

    // If either the layer or import modal are showing, don't show the tooltip
    const isTooltipAllowed = useRefEffectValue(!(modalLayerId || showImportLayer));

    const [{ tree, selectedNodeIds }, treeBrowserContext] = useTreeBrowserInitialise(
        { mainNodeId: mapId, treeId: mapId, sort: getSort(user?.userId), multiSelectRequiresCtrl: true },
        {
            ...sidebarNodeServices,
            moveNodes: async (nodes) => {
                const groups = nodes.filter((n) => n.type === NodeType.Folder);
                const layers = nodes.filter((n) => n.type === NodeType.Layer);

                if (groups.length > 0) {
                    groups.forEach((g) => patchMapGroup({ id: g.id, parentGroupId: mapId !== g.parentId ? g.parentId : null }, mapId));
                }
                if (layers.length > 0) {
                    await patchLayers(
                        mapId,
                        layers.map((l) => ({ id: l.id, groupId: mapId !== l.parentId ? l.parentId : null }))
                    );
                }
            },

            updateNode: async () => {
                throw new Error("Use updateNodeLocally instead");
            },
            getNode: async (id: string, _context: TreeBrowserState<Node>, sort?: TreeBrowserSort<Node>) => {
                const isLayer = layers.value.some((l) => l.id === id);
                // If it's a layer node, tell the store we are fetching all the geojson for that layer
                if (isLayer != null) {
                    dispatch(map.actions.updatePendingGeoJSONForLayer({ [id]: "all" }));
                }
                const { node, objects } = await getSidebarNode(id, _context, sort);
                if (objects?.length > 0) {
                    // When expanding layers in the sidebar, we need the object geometries for each of the objects, as this is not contained in the node data.
                    // This is used for displaying the tiny thumbnails of the objects.
                    // We choose not to override the object geometries here here, since this can affect drawing, making the drawings jump around the screen since the server data may be out of date.
                    // If we get map object geometry it needs to be added to undo/redo stack
                    const waypoints = await Promise.all(
                        objects.map<Promise<RouteWaypoint[]>>(async (object) => {
                            let waypoints: RouteWaypoint[] | null = null;
                            if ((object.modeOfTransport ?? object.geoJsonFeature?.properties?.modeOfTransport) != null) {
                                waypoints = await routeWaypointService.getRouteWaypoints(object.id, mapId);
                            }
                            return waypoints;
                        })
                    );
                    const localGeom = objects.map((object, index) => convertMapObjectToLocalGeoJson(object, waypoints[index]));
                    dispatch(map.actions.softlySetLayerGeometry(localGeom));
                }
                // If it's a layer node, tell the store we have finished fetching all the geojson for that layer
                if (node.type === NodeType.Layer) {
                    dispatch(map.actions.updatePendingGeoJSONForLayer({ [id]: [] }));
                }
                return node;
            },
            deleteNodes: async (nodes) => {
                // Given: nodes is the array of selected nodes
                const selectedNodeIds = nodes.map((n) => n.id);

                // Top-level groups: no parent in selection
                const topLevelGroups = nodes.filter((n) => n.type === NodeType.Folder && !selectedNodeIds.includes(n.parentId));
                // Top-level layers: no parent in selection
                const topLevelLayers = nodes.filter((n) => n.type === NodeType.Layer && !selectedNodeIds.includes(n.parentId));

                await Promise.all([...topLevelLayers.map((l) => deleteLayer(mapId, l.id)), ...topLevelGroups.map((g) => deleteMapGroup(mapId, g.id))]);
                return selectedNodeIds;
            },
        },
        (services) =>
            libraryWebSockets(
                services,
                [
                    SocketEvent.NODE_CREATED,
                    SocketEvent.NODE_DELETED,
                    SocketEvent.NODE_UPDATED,
                ] /* Since these are handled locally, ignore their socket events, as this causes conflicts when actions occur quickly */
            ),
        mapTreeBrowserSelection
    );

    // Tree can be using previous state when being used inside updateMapObjectNodeNames
    // Therefore use ref value to ensure it is always always using current state
    const treeRef = useRefEffectValue(tree);

    const mapNode = useMemo(() => (tree == null ? null : findNode(tree.childNodes, mapId)), [tree]);

    const updateMapObjectNodeNames = (mapObjects: MapObject[]) => {
        const mapObjectNameDataField = getMapObjectNameSystemDatafield(projectDataFields);
        mapObjects.forEach(({ id, dataFieldValues }) => {
            const node = findNode([treeRef.current], id);
            if (node != null) {
                treeBrowserContext.updateNodeLocally({ ...node, name: dataFieldValues[mapObjectNameDataField.id] });
            }
        });
    };

    useMapWebSockets(mapId);

    // True when the object can be cancelled instead of deleted
    const unsavedCompositionMode = useMemo(() => {
        const isCompositionMode = checkModeIsPresent(mode.value, "composition");
        // If map object is not selected therefore new composition or no remote map therefore point geometry or a copied map object
        const isNewObject = mapObjectsSelected.value.length === 0 || mapObjectsSelected.value.some((o) => localToRemoteMapObjectIdsMap.value[o.objectId] === undefined);
        return isCompositionMode && isNewObject;
    }, [mode.value, mapObjectsSelected.stamp, localToRemoteMapObjectIdsMap.stamp]);

    useEffect(() => {
        setCloseMapPopup(false);
    }, [mapObjectsSelected]);

    const dispatch: RootDispatch = useDispatch();

    useEffect(() => {
        const abortController = new AbortController();
        // Set current level
        const level = parseInt(queryParams.level, 10) || 0;
        dispatch(map.actions.updateSelectedLevel(level));

        const unitOfMeasurement = userLocalStorageManager.get(userLocalStorageItems.mapUnitOfMeasurement, user?.userId) ?? UnitOfMeasurement.Metric;
        dispatch(map.actions.setUnitOfMeasurement(unitOfMeasurement));

        loadMap(mapId, abortController.signal, dispatch);

        return () => {
            abortController.abort();
            // In dev environments where this function is ran twice, we want to retain the previous library ID after the map is navigated to
            const { previousLibraryId } = rootStore.getState().sidebarReducer;
            dispatch(resetSlice(SliceNames.SIDEBAR));
            dispatch(resetSlice(SliceNames.MAP));
            dispatch(sidebar.actions.setPreviousLibraryId(previousLibraryId));
        };
    }, []);

    const selectedAnalysisObjects = useMemo(() => {
        const selectedObjects: GeoJSON.Feature<AnySupportedGeometry, MapObjectProperties>[] = mapModule.mapObjectsSelected.value.reduce(
            (acc, { objectId, layerId }) => (isAnalysisLayer(layerId) ? [...acc, mapModule.geoJSON.value[layerId]?.find((obj) => obj.objectId === objectId)?.feature] : acc),
            []
        );

        return selectedObjects ?? [];
    }, [mapModule.mapObjectsSelected.stamp, mapModule.geoJSON.value]);

    const analysisLayers = useMemo(() => layers.value.filter((layer) => isAnalysisLayer(layer.id)), [layers.stamp]);

    const analysisLayersSelected = analysisLayers.some((layer) => layer.selected);

    const selectedMapObjectsWithGeom = useMemo(() => {
        const selectedObjects: GeoJSON.Feature<AnySupportedGeometry, MapObjectProperties>[] = mapModule.mapObjectsSelected.value.reduce<
            GeoJSON.Feature<AnySupportedGeometry, MapObjectProperties>[]
        >((acc, { objectId, layerId }) => {
            if (!Object.keys(mapModule.geoJSON.value).length) return acc;
            const foundObject = mapModule.geoJSON.value[layerId]?.find((obj) => obj.objectId === objectId)?.feature;
            if (foundObject) {
                acc.push(foundObject);
            }
            return acc;
        }, []);
        return selectedObjects ?? [];
    }, [mapModule.mapObjectsSelected.stamp, mapModule.geoJSON.stamp]);

    const areSelectedObjectsInLocalGeoJSON = selectedMapObjectsWithGeom.length === mapObjectsSelected.value.length;

    const startDrawing = (properties: Partial<Pick<MapObjectProperties, "fixedShape" | "modeOfTransport">>) => {
        const mapLayer = { ...layers.value.find((lyr) => lyr.selected), visible: true };
        drawMapObjectMetric(properties);
        eventStream.next({
            type: machineEventTypes.compose,
            payload: { objects: [{ layerId: mapLayer?.id, geojson: { properties }, waypoints: [] }] },
        });
        dispatch(map.actions.updateMapLayer(mapLayer));
    };

    const handleCancelDrawing = () => {
        eventStream.next({ type: machineEventTypes.cancelDrawing, payload: { updateStore: true } });
    };

    const deleteSelectedObjects = () => {
        if (objectChangesSaving.length > 0) {
            return;
        }
        setShowObjectDeletionDialog(true);
    };

    const isInAreaSelectMode = checkModeIsPresent(mode.value, "areaSelect");

    const handleAreaSelectClicked = () => {
        if (isInAreaSelectMode) {
            eventStream.next({ type: machineEventTypes.finish });
        } else {
            // Want to compose with the area select layer
            eventStream.next({
                type: machineEventTypes.enterAreaSelect,
                payload: { objects: [{ layerId: selectedLayerAreaSelectId, objectId: undefined, waypoints: undefined }] },
            });
        }
    };

    const handleZoomToObjectsClicked = () => {
        setFocusOnObjectsLoading(true);
        getMapBoundingBoxPolygon(mapId, currentLevel)
            .then((boundingBoxGeoJsonGeom) => {
                if (boundingBoxGeoJsonGeom != null && boundingBoxGeoJsonGeom?.coordinates != null) {
                    dispatch(map.actions.setBounds(boundingBoxGeoJsonGeom.coordinates));
                } else {
                    toast.info(
                        {
                            title: translate(Content.map2.controls.no_objects_to_zoom),
                            message: translate(Content.map2.controls.no_objects_to_zoom),
                            overridingToastIcon: "circle-info",
                        },
                        { autoHideDuration: 5000 }
                    );
                }
                setFocusOnObjectsLoading(false);
            })
            .catch(() => setFocusOnObjectsLoading(false));
    };

    // open/close resource when mounting/dismounting the map
    useResourceConnection([{ resourceId: mapId, sourceId: mapId, resourceType: ResourceTypes.MAP }]);

    const getMapContainerGridColumnStyle = () => {
        const leftMost = sidebarHidden ? "1" : "2";
        const rightMost = isMediumScreenSize ? "4" : "3";
        return `${leftMost} / ${rightMost}`;
    };

    const showObjectDetails =
        !selectedAnalysisObjects.length &&
        !isInAreaSelectMode &&
        checkAnyModeIsPresent(mode.value, ["read", "edit"]) &&
        !checkModeIsPresent(mode.value, "composition") &&
        sidebarContent !== SidebarContent.DATA_TABLE &&
        mapObjectsSelected.value.length > 0 &&
        showMapObjectInformation &&
        !closeMapPopup;

    const currentLayersOriginLayerIds = useMemo(() => findNodesByType(mapNode?.childNodes ?? [], NodeType.Layer)?.map((n) => n.sourceId) ?? [], [mapNode]);

    const [tourRunning, setTourRunning] = useState<boolean>(userPreferences.showMapTour);

    const selectedLayers = useMemo(() => getSelectedLayers(selectedNodeIds, layers.value), [selectedNodeIds, layers.stamp]);

    const selectedLayerSingle = selectedLayers.length === 1 ? selectedLayers[0] : null;

    const [focusOnObjectsLoading, setFocusOnObjectsLoading] = useState(false);
    const anyUserLayersPresentAndVisible: boolean = useMemo(() => layers.value != null && filterByUserLayers(layers.value).filter((l) => l.visible).length > 0, [layers]);

    /** Update the machine with latest layer permissions when they have changed */
    useEffect(() => {
        if (layers.value != null && mapNode != null) {
            const layerIds = layers.value.filter(({ id }) => isValidUuid(id)).map(({ id }) => id);
            const nodes = findNodes(mapNode.childNodes, layerIds, false);
            const permissionMap = {};
            const nodesMap = createMapFromArray(nodes, "id");
            layerIds.forEach((layerId) => {
                const node = nodesMap.get(layerId);
                // When a layer is missing a node assume they have just created, once the node has been fetched it will update
                permissionMap[layerId] = node?.authorisation != null ? node.authorisation : Authorisation.Admin;
            });
            eventStream.next({ type: machineEventTypes.setLayerPermission, payload: permissionMap });
        }
    }, [layers.stamp, mapNode?.childNodes, mapModule.mode.value, mappingEngine]);

    /** Can the user edit the current layer selection */
    const doesUserHavePermissionToDraw = useMemo(() => {
        if (selectedNodeIds.length === 0) {
            return true;
        }
        const selectedNodes = findNodes(mapNode?.childNodes ?? [], selectedNodeIds, false);
        const selectedLayerNodes = selectedNodes?.filter((node) => node.type === NodeType.Layer);
        return canEditAll(selectedLayerNodes);
    }, [selectedNodeIds, mapNode?.childNodes, layers.stamp]);

    const { hiddenLayersSelected, lockedLayersSelected } = useMemo(() => {
        let hiddenLayersSelected = false;
        let lockedLayersSelected = false;
        (getSelectedLayers(selectedNodeIds, layers.value) ?? []).forEach((layer) => {
            if (layer.visible === false) {
                hiddenLayersSelected = true;
            }
            if (layer.locked === true) {
                lockedLayersSelected = true;
            }
        });
        return { hiddenLayersSelected, lockedLayersSelected };
    }, [selectedNodeIds, layers?.stamp, mapNode?.childNodes]);

    const canUserEdit = doesUserHavePermissionToDraw && !lockedLayersSelected;

    const { isSplitLineValid, splitLineTooltip, isSplitting, confirmationModal } = useLineSplittingState(selectedMapObjectsWithGeom, selectedLayers, mode.value);

    const disableDelete = mapObjectsSelected.value.length === 0 || !areSelectedObjectsInLocalGeoJSON || unsavedCompositionMode || !canUserEdit || isSplitting;

    // Handle delete keyboard shortcuts
    useKeyboardEvent(
        (event) => {
            // If the target is not an input or a dialog, show the delete dialog
            if (
                !(
                    event.target instanceof HTMLInputElement ||
                    event.target instanceof HTMLTextAreaElement ||
                    (event.target as HTMLElement).className.includes("MuiDialog") ||
                    hasParentOfClass(event.target as HTMLElement, "MuiDialog")
                )
            ) {
                setShowObjectDeletionDialog(true);
            }
        },
        !disableDelete,
        ["Delete", "Backspace"],
        document
    );

    const isExternalUser = user?.userScope === UserScope.External;

    const addCommentMode = checkModeIsPresent(mode.value, "addComment");

    const isUserExternalAndViewComments = addCommentMode && isExternalUser;

    const getMapLayerPreviewUrl = useGetLayerThumbnailPreview();

    const isSnappingEnabled = drawingModifier === "snapping" && !isSplitting;

    const setSelectedToolbarButton = () => {
        if (checkModeIsPresent(mode.value, "areaSelect")) return ObjectSelectType.AreaSelect;
        if (checkModeIsPresent(mode.value, "composition")) {
            if (analysisLayersSelected || selectedAnalysisObjects.length !== 0) return selectedAnalysisTool;
        }
        if (checkModeIsPresent(mode.value, "composition") && selectedLayerSingle) {
            switch (selectedMapObjectsWithGeom[0]?.geometry.type) {
                case "Point":
                    return DrawingType.Point;
                case "LineString":
                    return DrawingType.Line;
                case "Polygon":
                    return DrawingType.Polygon;
                default:
                    return DrawingType.Polygon;
            }
        }
        if (checkModeIsPresent(mode.value, "splitLine")) return EditingAction.LineSplitting;
        if (addCommentMode) return CommentModeType.Commenting;
        // object select is the default
        return ObjectSelectType.ObjectSelect;
    };

    const isMapLoading = useMapLoading();

    return (
        <MapUndoRedoWrapper treeBrowserContext={treeBrowserContext} getCompositionUndoRedo={mapContainerRef.current?.getCompositionUndoRedo}>
            <MapTour
                running={tourRunning}
                onFinish={() => {
                    setTourRunning(false);
                    dispatch(updateUserPreferences({ showMapTour: false }));
                }}
            >
                <StyledPage leftWidth={`${sliderContextState.leftWidth}px`} isMediumScreenSize={isMediumScreenSize} ref={pageRef}>
                    <TreeBrowserContext.Provider value={treeBrowserContext}>
                        <SidebarErrorBoundary toast={toast}>
                            <StyledSideBar
                                sidebarHidden={sidebarHidden}
                                setSidebarHidden={setSidebarHidden}
                                windowWidth={windowWidth}
                                minWidth={minSidebarWidth}
                                mapId={mapId}
                                leftWidth={sliderContextState.leftWidth}
                                setLeftWidth={setLeftWidth}
                                setPopoverAnchor={setPopoverAnchor}
                                selectedAnalysisObjects={selectedAnalysisObjects}
                                showObjectDetails={showObjectDetails}
                                userId={user?.userId}
                                updateMapObjectNodeNames={updateMapObjectNodeNames}
                                layers={layers}
                                getMapPosition={mapContainerRef.current?.getMapPosition}
                                setShowObjectDeletionDialog={setShowObjectDeletionDialog}
                            />
                        </SidebarErrorBoundary>
                        <StyledSlider
                            data-testid="sidebar-slider"
                            sidebarHidden={sidebarHidden}
                            left={sliderContextState.leftWidth}
                            onMouseDown={onMouseDownOnSlider}
                            onDoubleClick={onDoubleClickOnSlider}
                        />
                        {isMapLoading ? (
                            <div style={{ position: "relative", gridRow: "1/4", gridColumn: getMapContainerGridColumnStyle() }}>
                                <LoadingComponent />
                            </div>
                        ) : failedToGetMap != null ? (
                            <div
                                style={{
                                    position: "relative",
                                    gridRow: "1/4",
                                    gridColumn: getMapContainerGridColumnStyle(),
                                    height: "100%",
                                    width: "100%",
                                    justifyContent: "center",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <ErrorLarge
                                    text={
                                        <Header3 className={textCenter}>
                                            {translate(Content.map6.get_map_errors[failedToGetMap])}.{" "}
                                            <span>
                                                <Link to="/spatial-planner">{translate(Content.map6.get_map_errors.click_here_to_browser_maps)}</Link>
                                            </span>
                                        </Header3>
                                    }
                                />
                            </div>
                        ) : (
                            <StyledMapContainer gridColumn={getMapContainerGridColumnStyle()}>
                                {webGLSupported ? (
                                    <MapContainer
                                        gridColumn={getMapContainerGridColumnStyle()}
                                        mappingEngine={mappingEngine}
                                        isSavedMapViewToolTipAllowed={isTooltipAllowed}
                                        projectDatafields={projectDataFields}
                                        ref={mapContainerRef}
                                        showDeleteModal={() => setShowObjectDeletionDialog(true)}
                                        mapPermission={mapNode?.authorisation}
                                        pageRef={pageRef}
                                        useLocalUnrealIp={USE_LOCAL_UNREAL_IP}
                                    />
                                ) : (
                                    <ErrorBoundaryFallbackWebGL />
                                )}
                                {mappingEngine === MappingEngine.Mapbox && (
                                    <MemoizedMapNavigationComponent
                                        setPopoverAnchor={setPopoverAnchor}
                                        windowWidth={windowWidth}
                                        mapId={mapId}
                                        showTooltip={isAdmin(mapNode?.authorisation)}
                                    />
                                )}
                                {savedMapViewWizard.stage === SavedMapViewStages.NOTIFICATION && (
                                    <MapViewsPopper
                                        existingMapView={savedMapViews ? savedMapViews.find((smv) => smv.id === savedMapViewWizard?.mapViewId) : null}
                                        submit={() => {
                                            dispatch(map.actions.setSavedViewMapWizard({ stage: SavedMapViewStages.FORM, id: savedMapViewWizard.mapViewId }));
                                        }}
                                        cancel={() => {
                                            dispatch(map.actions.setSavedViewMapWizard({ stage: SavedMapViewStages.NONE, id: undefined }));
                                        }}
                                    />
                                )}

                                {!isUserExternalAndViewComments && mappingEngine === MappingEngine.Mapbox && (
                                    <IventisErrorBoundaryWithToast size="overlay" toast={toast}>
                                        <DrawToolBarComponent
                                            position={{ top: "20px", left: "50%", translateX: "-50%" }}
                                            boxShadowColour={theme.shades.three}
                                            testId="draw-tool-bar-container"
                                        >
                                            <DrawToolBarButtons
                                                selectedFeatures={selectedMapObjectsWithGeom}
                                                layerType={selectedLayers[0]?.styleType}
                                                composingNewObject={unsavedCompositionMode}
                                                selectedButton={setSelectedToolbarButton()}
                                                eventStream={eventStream}
                                                commentsData={{
                                                    hideComments,
                                                }}
                                                deleteData={{
                                                    deleteSelectedObjects,
                                                    isDeleting: objectChangesSaving.length > 0,
                                                    disabled: disableDelete,
                                                }}
                                                isExternalUser={isExternalUser}
                                                drawingButtonData={{
                                                    canUserDraw:
                                                        canUserEdit && (checkAnyModeIsPresent(mode.value, ["composition", "areaSelect"]) || mapObjectsSelected.value.length > 0),
                                                    startDrawing,
                                                    disabled: hiddenLayersSelected || !canUserEdit || selectedLayerSingle == null,
                                                    handleCancelDrawing,
                                                    disableAnalysisCompleteButton:
                                                        !checkModeIsPresent(mode.value, "composition") ||
                                                        selectedAnalysisObjects.length === 0 ||
                                                        selectedAnalysisObjects?.find((object) => isCompositionValid(object.geometry)) == null,
                                                    disableDrawingCompleteButton:
                                                        !checkModeIsPresent(mode.value, "composition") ||
                                                        mapObjectsSelected.value.length === 0 ||
                                                        unsavedCompositionMode ||
                                                        selectedMapObjectsWithGeom?.find((object) => isCompositionValid(object.geometry)) == null,

                                                    handleRoutePlannerClick: () => {
                                                        if (showRoutePlanner) {
                                                            if (unsavedCompositionMode) {
                                                                eventStream.next({ type: machineEventTypes.finish });
                                                            } else {
                                                                treeBrowserContext.toggleSelectNodes([mapObjectsSelected.value[0]?.objectId], {
                                                                    from: FROM_MAP_ROOT,
                                                                    ignoreExistingSelection: false,
                                                                    simulateCtrlDown: true,
                                                                });
                                                            }
                                                        } else {
                                                            startDrawing({ modeOfTransport: ModeOfTransport.Car });
                                                        }
                                                    },
                                                }}
                                                copyButtonData={{
                                                    disabled:
                                                        addCommentMode ||
                                                        mapObjectsSelected.value.length === 0 ||
                                                        !areSelectedObjectsInLocalGeoJSON ||
                                                        selectedAnalysisObjects.length !== 0 ||
                                                        isPlacingGeometry ||
                                                        checkModeIsPresent(mode.value, "composition") ||
                                                        showRoutePlanner ||
                                                        !canUserEdit ||
                                                        isSplitting,
                                                }}
                                                onAnalysisToolClicked={(analysisTool) => {
                                                    dispatch(map.actions.setSelectedAnalysisTool(analysisTool));
                                                    analysisToolMonitoringMetric(analysisTool);
                                                    eventStream.next({
                                                        type: machineEventTypes.composeAnalysis,
                                                        payload: { objects: [{ layerId: isAnalysisToolLine(analysisTool) ? analysisLineLayerId : analysisPolygonLayerId }] },
                                                    });
                                                }}
                                                disableUndoRedo={isPlacingGeometry || addCommentMode || isSplitting}
                                                objectSelectData={{
                                                    handleAreaSelect: handleAreaSelectClicked,
                                                    layersSelected: selectedNodeIds.length !== 0,
                                                }}
                                                drawingToolTip={drawingToolTip}
                                                handleInfoTooltipClose={() => {
                                                    dispatch(map.actions.setDrawingToolTip(null));
                                                    if (userPreferences.showAddAnotherObjectToLayer) {
                                                        dispatch(updateUserPreferences({ showAddAnotherObjectToLayer: false }));
                                                    }
                                                }}
                                                handleSetSelectedAnalysisTool={(tool) => {
                                                    dispatch(map.actions.setSelectedAnalysisTool(tool));
                                                    analysisToolMonitoringMetric(tool);
                                                }}
                                                handleHideComments={() => dispatch(map.actions.setHideComments(!hideComments))}
                                                drawingModifiers={{
                                                    current: drawingModifier,
                                                    setModifier: (mod) => {
                                                        // If snapping is being turned on
                                                        if (!isSnappingEnabled) {
                                                            incrementMonitoringCount({ name: MetricName.SNAP_MAP_OBJECT_USED, resourceId: mapId });
                                                        }
                                                        eventStream.next({ type: machineEventTypes.setDrawingModifier, payload: mod });
                                                    },
                                                }}
                                                lineSplitting={{
                                                    disabled: !isSplitLineValid,
                                                    tooltip: splitLineTooltip,
                                                    onClick: () => {
                                                        if (isSplitting) {
                                                            eventStream.next({ type: "FINISH" });
                                                        } else {
                                                            eventStream.next({ type: "START_SPLITTING" });
                                                        }
                                                    },
                                                    cancel: () => {
                                                        eventStream.next({ type: "FINISH" });
                                                    },
                                                }}
                                            />
                                        </DrawToolBarComponent>
                                    </IventisErrorBoundaryWithToast>
                                )}
                                <VerticalToolbar>
                                    <VerticalToolbarErrorBoundary size="overlay" toast={toast}>
                                        <div>
                                            {windowWidth < screenSizeBreakpoints.medium &&
                                                !isExternalUser &&
                                                (commentsDrawingMode !== "composition" ? (
                                                    <CommentPanelButton
                                                        action={() => eventStream.next({ type: machineEventTypes.toggleComments })}
                                                        icon={<FontAwesomeIcon icon={["far", "message-lines"]} />}
                                                        text=""
                                                        selected={false}
                                                        style={panelbuttonStyle(theme)}
                                                        disabled={isPlacingGeometry || showRoutePlanner}
                                                    />
                                                ) : (
                                                    <CommentPanelButton
                                                        action={() => eventStream.next({ type: machineEventTypes.exitComments, keepCommentsMachineAlive: false })}
                                                        icon={<FontAwesomeIcon icon={["far", "xmark"]} />}
                                                        text={translate(Content.common.buttons.cancel)}
                                                        selected
                                                        style={panelbuttonStyle(theme)}
                                                        disabled={false}
                                                    />
                                                ))}
                                        </div>
                                        <hr />
                                        <div>
                                            {mappingEngine === MappingEngine.Unreal && (
                                                <BookmarksButton
                                                    style={panelbuttonStyle(theme)}
                                                    action={() => {
                                                        dispatch(map.actions.setShowBookmarks(!showBookmarks));
                                                    }}
                                                    selected={showBookmarks}
                                                    disabled={!userConnectedToDigitalTwin}
                                                />
                                            )}
                                        </div>
                                        <div>
                                            {windowWidth >= screenSizeBreakpoints.medium && functionality.digitalTwin && !isExternalUser && (
                                                <>
                                                    <VrPanelButton
                                                        isConnecting={isConnectingToDigitalTwin}
                                                        style={panelbuttonStyle(theme)}
                                                        action={async () => {
                                                            treeBrowserContext.toggleSelectNodes([], {
                                                                from: FROM_MAP_ROOT,
                                                                ignoreExistingSelection: true,
                                                            });
                                                            // Turn off any drawing modifiers
                                                            dispatch(map.actions.setDrawingModifier("none"));
                                                            // Turn on the instance on button click
                                                            if (!USE_LOCAL_UNREAL_IP && mappingEngine !== MappingEngine.Unreal) {
                                                                dispatch(connectToDigitalTwinLicense());
                                                                incrementMonitoringCount({ name: MetricName.DIGITAL_TWIN_USED });
                                                            } else if (mappingEngine !== MappingEngine.Unreal) {
                                                                dispatch(map.actions.setMappingEngine(MappingEngine.Unreal));
                                                            }

                                                            // If we are already using unreal, switch back to mapbox
                                                            if (mappingEngine === MappingEngine.Unreal) {
                                                                dispatch(map.actions.setMappingEngine(defaultMappingEngine));
                                                            }
                                                        }}
                                                        selected={mappingEngine === MappingEngine.Unreal}
                                                    />
                                                </>
                                            )}
                                        </div>
                                        <div>
                                            <AngleofViewButton
                                                style={mapcontrollbuttonStyle(theme)}
                                                action={() => eventStream.next({ type: machineEventTypes.setPitch })}
                                                isPitched={position.value.pitch > 0}
                                            />
                                            <hr />
                                            <SortPanelButton
                                                style={mapcontrollbuttonStyle(theme)}
                                                action={() => eventStream.next({ type: machineEventTypes.resetPosition })}
                                                selected={false}
                                                mapPosition={position.value}
                                            />
                                            {windowWidth >= screenSizeBreakpoints.medium && (
                                                <>
                                                    <hr />
                                                    <ZoomInPanelButton
                                                        style={mapcontrollbuttonStyle(theme)}
                                                        action={() => eventStream.next({ type: machineEventTypes.zoomIn })}
                                                        selected={false}
                                                        disabled={position.value.zoom === MAPBOX_MAX_ZOOM}
                                                    />
                                                    <ZoomOutPanelButton
                                                        style={mapcontrollbuttonStyle(theme)}
                                                        action={() => eventStream.next({ type: machineEventTypes.zoomOut })}
                                                        selected={false}
                                                        disabled={position.value.zoom === MAPBOX_MIN_ZOOM}
                                                    />
                                                </>
                                            )}
                                            {mappingEngine === MappingEngine.Mapbox && (
                                                <>
                                                    <hr />
                                                    <FocusOnObjectsButton
                                                        style={mapcontrollbuttonStyle(theme)}
                                                        action={handleZoomToObjectsClicked}
                                                        selected={false}
                                                        loading={focusOnObjectsLoading}
                                                        disabled={!anyUserLayersPresentAndVisible}
                                                    />
                                                    <hr />
                                                    <UserLocationButton
                                                        style={mapcontrollbuttonStyle(theme)}
                                                        action={() => eventStream.next({ type: MapEventTypes.GET_USER_LOCATION })}
                                                    />
                                                </>
                                            )}
                                        </div>{" "}
                                    </VerticalToolbarErrorBoundary>
                                </VerticalToolbar>
                            </StyledMapContainer>
                        )}
                        {checkModeIsPresent(mode.value, "edit") && showRoutePlanner && (
                            <MapRoutePlanner
                                operations={{
                                    updateRoute: (routeData) =>
                                        eventStream.next({
                                            type: machineEventTypes.updateRoute,
                                            payload: { ...routeData, updateStore: true },
                                        }),
                                    createNewRoute: (mode) => startDrawing({ modeOfTransport: mode ?? ModeOfTransport.Car }),
                                    convertToLine: () => {
                                        // This should be okay as the route planner only shows when one map object is selected
                                        const mapObjectId = mapObjectsSelected.value[0]?.objectId;
                                        const layerId = mapObjectsSelected.value[0]?.layerId;

                                        // Clear mode of transport
                                        dispatch(
                                            updateMapObject({
                                                mapObjectPatch: {
                                                    modeOfTransport: null,
                                                    id: mapObjectId,
                                                },
                                                mapId,
                                                layerId,
                                            })
                                        );

                                        // Remove the route time data field value
                                        const routeTimeDf = getMapObjectRouteTimeSystemDataField(projectDataFields);

                                        dispatch(
                                            patchMapObjectsDataFieldValues({
                                                mapObjects: [
                                                    {
                                                        id: mapObjectId,
                                                        layerId,
                                                        dataFieldValues: {
                                                            [routeTimeDf.id]: null,
                                                        },
                                                    },
                                                ],
                                            })
                                        );

                                        // Deselect the current object to close the route planner and ensure the machine is in the correct state for reseelction
                                        treeBrowserContext.toggleSelectNodes([], {
                                            from: FROM_MAP_ROOT,
                                            ignoreExistingSelection: true,
                                        });

                                        incrementMonitoringCount({ name: MetricName.ROUTE_CONVERT_TO_LINE });
                                    },
                                }}
                                unitOfMeasurement={mapModule.unitOfMeasurement.value}
                                composingNewRoute={checkModeIsPresent(mode.value, "composition")}
                                createSearch={(mapId, callbacks) => createSearch(mapId, callbacks, { getMapLayerPreviewUrl })}
                                mapId={mapId}
                                className="route-planner"
                                unsavedComposition={unsavedCompositionMode}
                            />
                        )}
                        <PopoverComponent sidebarPopoverAnchor={popoverAnchor} />
                        {savedMapViewWizard.stage === SavedMapViewStages.FORM && (
                            <MapViewsForm
                                cancel={() => {
                                    dispatch(map.actions.setSavedViewMapWizard({ stage: SavedMapViewStages.NONE, id: undefined }));
                                }}
                                mapViewId={savedMapViewWizard.mapViewId}
                                initialDefaultValue={
                                    savedMapViewWizard.mapViewId == null ? true : savedMapViewDefault !== null && savedMapViewDefault === savedMapViewWizard.mapViewId
                                }
                                initialNameValue={savedMapViewWizard.mapViewId ? savedMapViews.find((savedMapView) => savedMapView.id === savedMapViewWizard.mapViewId)?.name : ""}
                            />
                        )}
                        {mappingEngine === MappingEngine.Unreal && <PlanVenueBookmarksComponent getSnapshotDataUrl={mapContainerRef.current?.getSnapshotDataUrl} />}
                    </TreeBrowserContext.Provider>
                    {mappingEngine === MappingEngine.Mapbox && <PlanDateFilterComponent />}
                    {mappingEngine === MappingEngine.Unreal && userConnectedToDigitalTwin && <DigitalTwinCameraModeButtonsComponent />}
                </StyledPage>
                {showImportLayer && <ImportLayersDialog originLayerIds={currentLayersOriginLayerIds} />}
                {confirmationModal}
                <ConfirmationDialogComponent
                    show={showObjectDeletionDialog}
                    title={translate(Content.map2.object.deleteTitle)}
                    message={translate(Content.map2.object.deleteDescription)}
                    confirmText={translate(Content.common.buttons.confirm)}
                    cancelText={translate(Content.common.buttons.cancel)}
                    handleConfirm={() => {
                        const idsToDelete = mapObjectsSelected.value.map(({ objectId }) => objectId);
                        if (idsToDelete.length) {
                            eventStream.next({ type: machineEventTypes.deleteObject, payload: { updateStore: true } });
                        }
                        setShowObjectDeletionDialog(false);
                    }}
                    handleCancel={() => setShowObjectDeletionDialog(false)}
                />
            </MapTour>
        </MapUndoRedoWrapper>
    );
};

const SidebarMixin = css`
    grid-column: 1 / 4;

    ${media.medium} {
        grid-column: 1 / 2;
    }

    grid-row: 1 / 4;
`;

const SidebarErrorBoundary = styled(IventisErrorBoundaryWithToast)`
    ${SidebarMixin}
`;

const StyledSideBar = styled(SideBarComponent)`
    ${SidebarMixin}
`;

const VerticalToolbarErrorBoundary = styled(IventisErrorBoundaryWithToast)`
    width: 44px;
`;

const StyledSlider = styled.div<{ left: number; sidebarHidden: boolean }>`
    position: fixed;
    height: ${({ sidebarHidden }) => (sidebarHidden ? 0 : "100%")};
    /* Position left minus half the width so the handle exists centrally over the given position (with a 6px width) */
    left: ${({ left }) => `${left - 3}px`};
    /* Ensures it is above the sidebar */
    z-index: ${zIndexes.mapSidebar.root + 1};
    width: 6px;
    &:hover {
        cursor: ew-resize;
    }
`;

const StyledMapContainer = styled.div<{ gridColumn: string }>`
    position: relative;
    grid-row: 1 / 4;
    grid-column: ${(props) => props.gridColumn};
    width: 100%;
    height: 100%;
`;

const StyledPage = styled.div<{ leftWidth: string; isMediumScreenSize: boolean }>`
    ${mapPageMixin}
    grid-template-rows: 72px auto 206px;

    .popup {
        grid-column: 1 / 2;
        grid-row: 1 / 4;
        background-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued50};
        color: ${({ theme }) => theme.typographyColors.blank};
        z-index: 500;

        ${customMediaQueryMax(screenSizeBreakpoints.medium)} {
            width: 100vw;
        }

        .selected-tab {
            border-bottom: 2px solid ${({ theme }) => theme.typographyColors.blank};
        }
    }

    .route-planner {
        width: 300px;
        position: absolute;
        right: 54px;
        top: 80px;
        max-height: calc(100% - 180px);
    }

    .venue-bookmarks {
        position: absolute;
        bottom: 135px;
        right: 10px;
        width: 350px;
        max-height: 600px;
        ${media.medium} {
            bottom: 140px;
            right: 60px;
        }
    }
`;

/**
 * Gets the layers which are selected from the map state
 * @param {string[]} selectedNodeIds - ids of the layers which are selected
 * @param {DomainLayer[]} mapLayers - array of map layers
 * @returns  {DomainLayer[]} - array of map layers which are selected
 */
export function getSelectedLayers(selectedNodeIds: string[], mapLayers: DomainLayer[]): DomainLayer[] {
    return mapLayers.filter((mapLayer) => selectedNodeIds.includes(mapLayer.id)).sort((a, b) => a.sidebarOrder - b.sidebarOrder);
}

export default MapRootPage;
